import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { createElement } from "commonAssets/lib/createElement";
import { modal } from "commonAssets/lib/modal";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { i18n, loading, outputDiv } from "commonAssets/js/constants";
import formDefault from "../../docs/formDefault.json";

/**
 * Flatten Tagify JSON values to comma-separated strings for display.
 * e.g. [{"value":"Peter"},{"value":"Paul"}] → "Peter, Paul"
 */
function formatTagifyValue(value) {
    if (typeof value === "string") {
        try {
            value = JSON.parse(value);
        } catch {
            const el = createElement("div", { class: "col" });
            el.textContent = value;
            return el;
        }
    }
    if (
        Array.isArray(value) &&
        value.every((item) => item && typeof item === "object" && "value" in item)
    ) {
        const formatted = value.map((item) => item.value).join(", ");
        const el = createElement("div", { class: "col" });
        el.textContent = formatted;
        return el;
    }
    const el = createElement("div", { class: "col" });
    el.textContent = JSON.stringify(value);
    return el;
}

export class customSubmitClaim extends SubmitClaim {
    async form() {
        const form = formDefault.claim_form[this.lang];
        this.init_claim(form);
    }

    /**
     * Deficiency (Fix Claim) portal gate. WP-1459.
     *
     * Literal copy of the shared SubmitClaim.init_claim with two site-local
     * changes: (1) the stock #claim_form_alert is removed after the guard
     * renders, and (2) a deficiency gate is inserted in the classMemberPost
     * success block. Only class members with an active "Deficient" status that
     * is still editable may proceed; everyone else is stopped before the form
     * renders. Fails closed.
     */
    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    // Deficiency portal: drop the stock claim-form alert.
                    document.getElementById("claim_form_alert")?.remove();

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            document.getElementById("guard").remove();
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (POST keeps codes private)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Deficiency gate (WP-1459): correction-only.
                                    // Allow only an active "Deficient" status
                                    // that is still editable. Fail closed.
                                    const cm_data = result.data.claim_id
                                        ? result.data.class_member?.cm_data
                                        : result.data.cm_data;
                                    const statuses =
                                        cm_data?.ClassMemberStatus ?? [];
                                    const canFixClaim = statuses.some(
                                        (status) =>
                                            status?.Active === true &&
                                            status?.Label === "Deficient" &&
                                            status?.CanEdit === true
                                    );

                                    if (!canFixClaim) {
                                        loadingGif(loading, "hide", 250);
                                        modal(
                                            i18n.getTranslation("error"),
                                            `<p class="mb-0 text-center">This portal is not open to new claims. If you received a deficiency notice, please contact the settlement administrator for assistance.</p>`
                                        );
                                        return;
                                    }

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0];

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    this.edit_claim(form);

                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    confirmation_claim_fields(params) {
        const fields = params.fields;
        const fieldsContainer = createElement("div", {
            class: "mb-3",
            id: "confirmation_claim_fields",
        });
        Object.keys(fields).forEach((key) => {
            const fieldLabel = i18n.getField(key);
            if (
                !params.confirmation.claim_details.excluded_keys.includes(key)
            ) {
                if (fields[key]) {
                    const labelEl = createElement("div", {
                        class: "col fw-bold text-end",
                    });
                    labelEl.textContent = fieldLabel;
                    const row = createElement(
                        "div",
                        { class: "row mb-3 pb-3 border-bottom" },
                        [labelEl, formatTagifyValue(fields[key])]
                    );
                    fieldsContainer.appendChild(row);
                }
            }
        });
        return fieldsContainer;
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new customSubmitClaim(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }
}
